<template>
  <div class="app-body_show container-fluid pt-4 pb-5">
    <div class="row">
      <div class="col-md-3">
        <template v-if="investorFundsData">
          <label-card v-if="!isEmpty(investorFundsData.investment_stages)" :title="`Investment Stages`"
            :labels="investorFundsData.investment_stages" />
          <label-card v-if="!isEmpty(investorFundsData.first_investment_ticket_sizes)" :title="`First Investment Size`"
            :labels="[`${$options.filters.formatNumberShort(investorFundsData.first_investment_ticket_sizes[0].min)} - ${$options.filters.formatNumberShort(investorFundsData.first_investment_ticket_sizes[0].max)}`]" />
          <label-card v-if="!isEmpty(investorFundsData.investment_areas)" :title="`Investment Areas`"
            :labels="investorFundsData.investment_areas" />
          <div class="card mb-4 card-body py-3" v-if="investorFundsData.investment_criteria_url">
            <h3 class="mb-0 t-400">
              Investment Criteria
            </h3>
            <div class="mt-3">
              <ul class="list-unstyled mb-0 flex-wrap d-flex">
                <li class="word-overflow position-relative">
                  <i class="fa fa-link t-secondary t-sm width--20px mr-1" />
                  <a :href="investorFundsData.investment_criteria_url | formatWebsite" target="_blank">Investment
                    Criteria</a>
                </li>
              </ul>
            </div>
          </div>
          <keywords v-if="organisation.tags" :organisation="organisation" :isLoaded="isLoaded" class="card mb-4" />
        </template>
        <!-- render organisation leaderships -->
        <leaderships :organisation="organisation" :isLoaded="isLoaded" class="card mb-4">
        </leaderships>
        <!--
          Show component while data is loading,
          if data is loaded and no grph data present, hide it
        -->
        <template v-if="!isIncubator && !isInvestor">
          <flex-chart v-if="!isGraphLoaded || !(isGraphLoaded && !graphData.publications)"
            :isLoaded="!!(isGraphLoaded && graphData.publications)" :graphData="parsedGraphData.publications || []"
            :legend="parsedGraphData.publicationsLegend || {}" :name="'Publications'" :scope="'organisations'"
            :chartType="'bar'" :color="'#43AEC5'" isSmall withContainer>
          </flex-chart>

          <flex-chart v-if="!isGraphLoaded || !(isGraphLoaded && !graphData.citations)"
            :isLoaded="!!(isGraphLoaded && graphData.citations)" :graphData="parsedGraphData.citations || []"
            :legend="parsedGraphData.citationsLegend || {}" :name="'Citations'" :scope="'organisations'"
            :chartType="'line'" :color="'#EFA9BB'" isSmall withContainer>
          </flex-chart>
        </template>

        <keywords v-if="!investorFundsData" :organisation="organisation" :isLoaded="isLoaded" class="card mb-4" />
      </div>
      <div class="col-md-6">
        <bar-search-categories v-if="!isIncubator && !isInvestor" :indexes="indexes" :routeName="'organisation.explore'"
          :isSticky="false" :indexCount="6" :showEmptyBar="false" />
        <search-bar v-if="isLoaded && hasResearch" class="mb-4" :state="searchFilterObject" :indexes="indexes" />

        <div v-if="isLoaded && isBeingEnriched" class="card">
          <div class="card-body py-3 t-primary">
            We are in the process of enriching this page, additional information will be available soon!
          </div>
        </div>

        <!-- render profile AI -->
        <ask-this-profile v-if="isLoaded && hasResearch"
          :profileType="getMostRelevantOrganisationCategory(organisation.organisation_types)"
          :searchFilterObject="searchFilterObject"
          :extraData="{'about':organisation.about}"
          :searchIndices="['publications', 'patents', 'projects', 'clinical_trials', 'news', 'web_pages_organisations']"
        />

        <!-- render organisation about -->
        <about :organisation="organisation" :isLoaded="isLoaded" class="card mb-4">
        </about>

        <tree-chart v-if="!isIncubator && !isInvestor" :isLoaded="isTreeGraphLoaded" :graphData="treeGraphData" :parent="{
          label: organisation.name,
          type: organisation.category,
          value: organisation.slug_text
        }" :type="'organisation'" :exploreRoute="exploreRoute" />

        <!-- render organisation technologies -->
        <technologies :organisation="organisation" :isLoaded="isLoaded" class="card mb-4">
        </technologies>

        <webpages-container-preview v-if="shouldShowWebpagesPreview" :searchFilterObject="searchFilterObject"
          :index="'web_pages_organisations'" :route="{ name: 'organisation', slug: organisation.slug_values[0] }"
          :totalDocuments="3" class="mt-4" />

        <search-container-preview v-if="isLoaded" :searchFilterObject="searchFilterObject"
          :indexes="isIncubator || isInvestor ? ['organisations'] : ['patents', 'projects', 'publications', 'clinical_trials']"
          :suggestionIndexes="isIncubator || isInvestor ? [] : ['projects', 'publications', 'clinical_trials', 'patents']"
          :route="{ name: 'organisation', slug: organisation.slug_values[0] }" :scope="'organisations'"
          :innerTitleOverride="isIncubator || isInvestor ? 'Portfolio' : ''" class="mt-4" />

        <search-container-preview v-if="hasSpinoffs && isLoaded"
          :searchFilterObject="{ ...searchFilterObject, key: 'spinoff_from' }" :indexes="['organisations']"
          :suggestionIndexes="[]" :route="{ name: 'organisation', slug: organisation.slug_values[0] }"
          :scope="'organisations'" :innerTitleOverride="'Spinoffs'" :showViewAll="false" class="mt-4" />

      </div>
      <div class="col-md-3">
        <profile-actions scope="organisations" :item="organisation" :contacts="[]" :isLoaded="isLoaded" />

        <information :isLoaded="isLoaded" :organisation="organisation" class="card mb-4" />

        <search-container-preview v-if="isLoaded"
          :searchFilterObject="searchFilterObject"
          :indexes="['news']"
          :suggestionIndexes="['news']"
          :route="{ name: 'organisation', slug: organisation.slug_values[0] }"
          :scope="'organisations'"
          :innerTitleOverride="'News'"
          :totalDocuments="3"
          isNewsPreview
          class="mt-4"
        />

        <identifiers :item="organisation" />
      </div>
    </div>
  </div>
</template>

<script>
import FlexChart from '@/components/shared/chart/FlexChart'
import SearchBar from '@/components/search/BarDashboard'
import SearchContainerPreview from '@/containers/SearchPreview'
import About from '@/components/organisation/show/About'
import AskThisProfile from '@/components/llm/AskThisProfile'
import Technologies from '@/components/organisation/show/Technologies'
import Leaderships from '@/components/organisation/show/Leaderships'
import Information from '@/components/organisation/show/Information'
import Keywords from '@/components/organisation/show/Keywords'
import TreeChart from '@/components/shared/chart/Tree'
import withTree from '@/components/shared/chart/withTree'
import LabelCard from '@/components/shared/label/LabelCard'
import BarSearchCategories from '@/components/search/BarSearchCategories'
import Identifiers from '@/components/qa/Identifiers'
import ProfileActions from '@/components/shared/profile/Actions'
import WebpagesContainerPreview from '@/containers/WebpagesPreview'

import SearchService from '@/services/search'
import searchHelpers from '@/mixins/search/helpers'
import organisationMixins from '@/mixins/organisation'

export default {
  name: 'organisationShow',
  mixins: [searchHelpers, organisationMixins, withTree],
  components: {
    FlexChart,
    SearchBar,
    SearchContainerPreview,
    About,
    AskThisProfile,
    Technologies,
    Leaderships,
    Information,
    Keywords,
    TreeChart,
    LabelCard,
    BarSearchCategories,
    Identifiers,
    ProfileActions,
    WebpagesContainerPreview
  },
  props: {
    isLoaded: {
      type: Boolean,
      required: true
    },
    organisation: {
      type: Object,
      required: true
    },
    searchFilterObject: {
      type: Object,
      required: true
    },
    hasResearch: {
      type: Boolean,
      required: true
    },
    researchLoaded: {
      type: Boolean,
      required: true
    },
    indexes: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isGraphLoaded: false,
      graphData: {},
      isNetworkGraphLoaded: false,
      networkGraphData: []
    }
  },
  computed: {
    shouldShowWebpagesPreview () {
      if (
        !this.isLoaded ||
        !this.organisation ||
        this.organisation.category !== 'corporate' ||
        this.organisation.organisation_types.indexOf('Investor') > -1 ||
        this.organisation.organisation_types.indexOf('Incubator') > -1
      ) {
        return false
      }
      return true
    },
    isBeingEnriched () {
      return this.isLoaded && this.researchLoaded && !this.hasResearch && !this.organisation.about && !this.organisation.leaderships
    },
    parsedGraphData () {
      if (!this.graphData || this.isEmpty(this.graphData)) {
        return {}
      }
      const pubs = this.formatGraphData(this.graphData.publications)
      const cits = this.formatGraphData(this.graphData.citations)
      const firstPub = pubs.findIndex(p => p.y > 0)
      const firstCit = cits.findIndex(p => p.y > 0)
      const earliestIndex = firstPub < firstCit ? firstPub : firstCit
      return {
        publicationsLegend: this.getTotals(this.graphData.publications),
        citationsLegend: this.getTotals(this.graphData.citations),
        publications: pubs.slice(Math.max(earliestIndex, 0)),
        citations: cits.slice(Math.max(earliestIndex, 0))
      }
    }
  },
  mounted () {
    if (this.isLoaded && !this.isIncubator && !this.isInvestor) {
      this.fetchGraphData()
      this.fetchTreeGraphData(this.getSearchObjectGraphData(this.searchFilterObject, ['publications']))
    }
  },
  watch: {
    isLoaded (newVal, oldVal) {
      if (newVal !== oldVal && newVal && !this.isIncubator && !this.isInvestor) {
        this.fetchGraphData()
        this.fetchTreeGraphData(this.getSearchObjectGraphData(this.searchFilterObject, ['publications']))
      }
    }
  },
  methods: {
    getTotals (data) {
      let total = 0
      let suggestions = 0
      data.forEach(item => {
        total += item.total
        suggestions += item.total_suggestions
      })
      return {
        total,
        suggestions
      }
    },
    formatGraphData (data) {
      if (!data) {
        return []
      }
      const items = data
        .sort((a, b) => b.year - a.year)
        .reduce((a, b, i, arr) => {
          // If there is no year, or it's super long ago just go to the next value
          if (!b.year || b.year < 1970) return a
          const prevYear = i && arr[i - 1].year
          // Check if there are any gaps between years, add missing dataPoints if needed
          if (prevYear && prevYear < b.year - 1) {
            const count = b.year - prevYear - 1
            for (let j = 0; j < count; j++) {
              a.push({
                x: prevYear + j + 1,
                y: 0
              })
            }
          }
          // Include suggestion counts into the graph data
          a.push({
            x: b.year,
            y: b.count
          })
          return a
        }, [])
      return items.map(i => {
        return {
          ...i,
          year: i.x.toString()
        }
      })
    },
    fetchGraphData () {
      this.graphData = {}
      this.isGraphLoaded = false
      const searchObject = this.getSearchObjectGraphData(this.searchFilterObject, ['publications'])
      SearchService.graphData(searchObject).then(graphData => {
        this.graphData = graphData
        this.isGraphLoaded = true
      }).catch(err => {
        this.error = this.handleError(err)
        this.isGraphLoaded = true
      })
    },
    exploreRoute (bucket) {
      return {
        name: `organisation.explore`,
        params: {
          category: this.searchFilterObject.index,
          buckets: [`collaborating_organisations:${bucket}`]
        }
      }
    }
  }
}
</script>
